/**
 * 한국어 사전 (Korean dictionary).
 *
 * This file is typed as `Dictionary`, so a key present in `./en` and absent
 * here is a compile error. It cannot silently fall back to English.
 *
 * Translation rules that are requirements, not preferences:
 *
 * - 식별(identified) and 게임 보고(reported) stay two different counts on the
 *   Members screen. The gap is a hole in *our view*; the members did not leave.
 * - "이력 부족" never becomes "0" or "성장 없음". Refusing to compute a trend
 *   and measuring a trend of zero are different claims.
 * - `미입력` (operator never typed it) stays visually and lexically distinct
 *   from a real `0`.
 * - Upgrade-impact copy uses only the hedges spec §27 mandates: `~와 연관됨`,
 *   `~ 이후 관측됨`, `기여 가능성`, `통제된 샘플 부족`. Never `~때문에` or
 *   `~로 인해` — those assert causation this data cannot support.
 * - Inference stays marked as inference (§1.1, §18): 추정/추론, not assertion.
 * - Attendance copy is for staffing, never for ranking people (§50). No
 *   `무단결석`, no `지각`, no blame vocabulary.
 * - The five freshness clocks stay separately labelled (§64). They are never
 *   merged into one "업데이트".
 *
 * Game terms Korean players already use in English stay in English: Bear Trap,
 * Rally, Alliance Championship, Swordland, Fortress, Sanctuary, Castle, Viking
 * Vengeance, KvK, R4/R5, TC.
 */

import type { Dictionary } from './dictionary';

export const ko: Dictionary = {
  meta: {
    localeName: '한국어',
    htmlLang: 'ko',
    intlLocale: 'ko-KR',
  },

  common: {
    unknown: '알 수 없음',
    notRecorded: '미기록',
    notRecordedTitle: '미기록',
    notEntered: '미입력',
    none: '없음',
    dash: '—',
    unnamedPlayer: '이름 없는 플레이어',
    unnamedAlliance: '이름 없는 연맹',
    unnamedLead: '이름 없는 리드',
    untitled: '제목 없음',
    notIdentified: '식별되지 않음',
    noAlliance: '연맹 없음',
    notSynced: '동기화 안 됨',
    /** 값이 0이라는 뜻이 아니라, 추세를 산출하지 않겠다는 뜻이다. */
    notEnoughHistory: '이력 부족 (산출 불가)',
    notScored: '점수 없음',
    notClassified: '분류 안 됨',
    notCalculable: '산출 불가',
    notRanked: '순위 없음',
    observed: '관측값',
    player: '플레이어',
    alliance: '연맹',
    power: '전투력',
    kills: '처치 수',
    level: '레벨',
    vip: 'VIP',
    tc: 'TC',
    townCentreLevel: 'TC 레벨',
    members: '멤버',
    wave: '웨이브',
    confidence: '신뢰도',
    samples: '샘플',
    why: '근거',
    close: '닫기',
    hide: '숨기기',
    open: '열기',
    cancel: '취소',
    saving: '저장 중…',
    discard: '버리기',
    or: '또는',
    tryAgain: '다시 시도',
    selectAll: '전체 선택',
    clear: '선택 해제',
    selectPlaceholder: '선택…',
    optional: '선택 입력',
    openSyncStatus: '동기화 상태 열기',
    noCoreAllianceTitle: '기준 연맹이 설정되지 않음',
    loadingAlliance: '연맹 불러오는 중',
    loadingRoster: '명단 불러오는 중',
    loadingState: '스테이트 불러오는 중',
    nothingToShow: '표시할 내용이 없습니다.',
    days: (days: number) => `${days}일`,
  },

  duration: {
    seconds: (seconds: number) => `${seconds}초`,
    minutes: (minutes: number) => `${minutes}분`,
    minutesSeconds: (minutes: number, seconds: number) => `${minutes}분 ${seconds}초`,
    hoursMinutes: (hours: number, minutes: number) => `${hours}시간 ${minutes}분`,
  },

  /**
   * `상위 N%` 로 쓰지 않는다. 72백분위는 상위 72%가 아니라 상위 28%이므로,
   * 뒤집힌 표기는 값을 조용히 반대로 읽게 만든다. 척도를 그대로 이름 붙인다.
   */
  percentile: (value: number, ordinalForm: string) => `${value}백분위수`,

  /**
   * 대시보드 경고 (스펙 §58).
   *
   * 서버가 `code` 와 `params` 를 함께 보내므로, 완성된 영어 문장을 받아 쓰는
   * 대신 운영자의 언어로 다시 조립한다. 이 표에 없는 코드(서버의 `UNKNOWN`
   * 포함)는 빈칸이 아니라 서버의 `message` 를 그대로 표시한다.
   *
   * 한국어에는 복수 표시가 없으므로 영어의 "(s)" 를 옮기지 않고 단위 명사
   * `건` 으로 자연스럽게 센다.
   */
  dashboardWarnings: {
    NO_CORE_ALLIANCE:
      '기준 연맹이 설정되지 않았습니다. 연맹 화면을 사용하려면 설정에서 CORE_ALLIANCE_ID 를 지정하세요.',
    CORE_ALLIANCE_NEVER_SYNCED: (allianceId: string) =>
      `기준 연맹 ${allianceId} 이(가) 설정되어 있지만 아직 한 번도 동기화되지 않았습니다.`,
    /** @param age 서버가 보낸 짧은 경과 표기. 예: "54m". */
    CORE_ROSTER_STALE: (age: string) =>
      `기준 연맹 명단을 마지막으로 가져온 지 ${age} 지났습니다.`,
    /** `추정치` 를 유지한다. 측정된 사용량이 아니라 추정값이다. */
    API_BUDGET: (used: string, budget: string) =>
      `Jeabs 일일 요청 추정치가 ${budget}건 중 ${used}건입니다. 예산을 다 쓰면 동기화가 중단됩니다.`,
    SYNC_FAILURES: (failures: number) =>
      `최근 6시간 동안 동기화 작업 ${failures}건이 실패했습니다. 자세한 내용은 동기화 상태 화면에서 확인하세요.`,
  },

  enums: {
    detectedEvent: {
      PLAYER_JOINED_ALLIANCE: '연맹 가입',
      PLAYER_LEFT_ALLIANCE: '연맹 탈퇴',
      PLAYER_TRANSFERRED_ALLIANCE: '연맹 이동',
      PLAYER_RENAMED: '닉네임 변경',
      TC_UPGRADE: 'TC 업그레이드',
      POWER_SPIKE: '전투력 급증',
      PLAYER_DORMANT: '활동 중단',
      PLAYER_REACTIVATED: '활동 재개',
    },
    eventType: {
      BEAR_TRAP: 'Bear Trap',
      ALLIANCE_CHAMPIONSHIP: 'Alliance Championship',
      SWORDLAND: 'Swordland',
      SANCTUARY: 'Sanctuary',
      FORTRESS: 'Fortress',
      CASTLE: 'Castle',
      VIKING_VENGEANCE: 'Viking Vengeance',
      TRI_ALLIANCE_CLASH: 'Tri-Alliance Clash',
      ALLIANCE_MOBILIZATION: 'Alliance Mobilization',
      ALLIANCE_BRAWL: 'Alliance Brawl',
      KVK: 'KvK',
      CUSTOM: '사용자 지정',
    },
    eventStatus: {
      PLANNED: '예정',
      OPEN: '모집 중',
      IN_PROGRESS: '진행 중',
      COMPLETED: '완료',
      CANCELLED: '취소됨',
      /** 대시보드의 status 는 열린 문자열이며 실제로 이 값을 보낸다. */
      DRAFT: '초안',
    },
    /**
     * 인원 배치를 위한 기록이며, 구성원을 서열화하기 위한 것이 아니다 (§50).
     * `무단결석`·`지각` 같은 질책성 표현은 쓰지 않는다.
     */
    attendanceStatus: {
      REGISTERED: '신청함',
      CONFIRMED: '참여 확정',
      ATTENDED: '참여함',
      LATE: '늦게 합류',
      NO_SHOW: '참여하지 않음',
      COMPLETED_ROLE: '역할 완료',
      EXCUSED: '사전 양해',
    },
    growthFlag: {
      WHALE: '고래',
      FAST_GROWER: '빠른 성장',
      RISING: '상승세',
      NORMAL: '보통',
      DORMANT: '휴면',
    },
    rallyRole: {
      BEAR_OFFENSE: 'Bear 공격',
      PVP_ATTACK: 'PvP 공격',
      PVP_GARRISON: 'PvP 수비대',
      PVP_JOINER: 'PvP 참여',
      CASTLE_ATTACK: 'Castle 공격',
      CASTLE_GARRISON: 'Castle 수비대',
      FORTRESS_ATTACK: 'Fortress 공격',
      FORTRESS_GARRISON: 'Fortress 수비대',
      SANCTUARY_ATTACK: 'Sanctuary 공격',
      SANCTUARY_GARRISON: 'Sanctuary 수비대',
    },
  },

  shell: {
    brand: 'Alliance Ops',
    brandSub: 'Kingshot',
    navLabel: '주 메뉴',
    devBanner:
      '개발용 픽스처가 활성화되어 있습니다 — 화면의 모든 수치는 합성 데이터이며, 실제 연맹 API에 연결되어 있지 않습니다.',
    signedIn: '로그인됨',
    noLinkedPlayer: '연결된 플레이어 없음',
    signOut: '로그아웃',
    languageLabel: '언어',
    navToggle: '메뉴 열기',
    navClose: '메뉴 닫기',
    themeLabel: '테마',
    theme: {
      system: '시스템',
      light: '라이트',
      dark: '다크',
    },
    restoringSession: '세션 복원 중',
  },

  nav: {
    groups: {
      alliance: '연맹',
      bear: 'Bear Trap',
      events: '작전',
      stateIntel: '스테이트 정보',
      data: '데이터',
    },
    overview: '개요',
    members: '멤버',
    growth: '성장',
    activity: '활동',
    roles: '역할',
    personalAnalyzer: '개인 분석',
    alliancePlanner: '연맹 플래너',
    bearHistory: '기록',
    experiments: '실험',
    rallyLab: 'Rally 랩',
    championship: 'Championship',
    swordland: 'Swordland',
    fortress: 'Sanctuary / Fortress',
    castle: 'Castle',
    viking: 'Viking',
    triAlliance: 'Tri-Alliance',
    mobilization: 'Mobilization / Brawl',
    stateAlliances: '연맹 목록',
    statePlayers: '플레이어',
    transfers: '이동',
    leaderboards: '리더보드',
    kvk: 'KvK',
    freshness: '최신성',
    rules: '규칙',
    catalog: '카탈로그',
    syncStatus: '동기화 상태',
    settings: '설정',
  },

  auth: {
    title: 'Alliance Ops',
    blurb: '비공개 연맹 운영 도구입니다. R5가 발급한 계정으로 로그인하세요.',
    email: '이메일',
    password: '비밀번호',
    signIn: '로그인',
    signingIn: '로그인 중…',
    signInFailed: '로그인에 실패했습니다.',
  },

  states: {
    loading: '불러오는 중',
    noDataTitle: '데이터 없음',
    noDataBody: 'API가 이 화면에 대해 아무것도 반환하지 않았습니다.',
    notAvailableYetTitle: (feature: string) => `${feature} — 아직 제공되지 않음`,
    notAvailableYetBody: (phase: string) =>
      `이 화면의 데이터 소스는 ${phase}에서 제공됩니다. 그 전까지는 아무것도 표시하지 않습니다 — 여기에 수치를 채우려면 지어내야 하고, 지어낸 수치는 빈칸보다 나쁩니다.`,
    missingRouteTitle: '이 데이터 소스는 아직 가동되지 않았습니다',
    missingRouteBody:
      'API가 해당 경로는 존재하지 않는다고 응답했습니다. 백엔드가 순차적으로 배포되는 동안에는 정상적인 상태이며, 계정에는 아무 문제가 없습니다.',
    genericErrorTitle: '문제가 발생했습니다',
    unexpectedError: '예기치 않은 오류가 발생했습니다.',
    errorTitles: {
      UNAUTHENTICATED: '로그아웃됨',
      FORBIDDEN: '권한 없음',
      RATE_LIMIT: '상위 API 요청 한도 도달',
      /** 대시보드가 근거 없는 수치를 그리기 시작하는 유일한 실패 모드이므로 정확히 이름 붙인다. */
      CONTRACT_MISMATCH: 'API와 이 앱이 응답 형식을 서로 다르게 알고 있습니다',
      NETWORK: 'API에 연결할 수 없음',
      REQUEST_FAILED: '요청 실패',
    },
  },

  api: {
    sessionExpired: '세션이 만료되었습니다. 다시 로그인하세요.',
    unreachable:
      '연맹 API에 연결하지 못했습니다. 네트워크 연결과 Worker 실행 여부를 확인하세요.',
    notJson: 'API가 JSON이 아닌 응답을 반환했습니다.',
    contractMismatch: (path: string) => `${path} 에 대한 API 응답이 공유 스키마와 일치하지 않습니다.`,
    forbidden: '이 계정에는 해당 항목에 대한 접근 권한이 없습니다.',
    notFound: '해당 리소스는 아직 존재하지 않습니다.',
    httpStatus: (status: number) => `API가 HTTP ${status} 를 반환했습니다.`,
    bearAnalysisContractMismatch: (playerId: string) =>
      `플레이어 ${playerId} 의 Bear 분석이 공유 스키마와 일치하지 않습니다.`,
  },

  config: {
    missing: (name: string) =>
      `${name} 이(가) 없습니다. apps/web/.env.example 을 apps/web/.env 로 복사한 뒤 값을 채우세요.`,
    notConfigured: '앱이 설정되지 않았습니다.',
  },

  quality: {
    confirmed: (field: string) => `${field}: 최신 상위 응답에서 직접 관측된 값입니다.`,
    possible: (field: string) =>
      `${field}: 상위 API가 보고한 값이지만, "아직 보강되지 않음"일 때도 같은 값을 반환합니다. 실제 측정값이 아닐 수 있습니다.`,
    unknown: (field: string) =>
      `${field}: 이 값이 상위 API에서 무엇을 뜻하는지 확인되지 않았습니다. 이 값을 근거로 판단하지 마세요.`,
    stale: (field: string) =>
      `${field}: 실제 측정값이지만, 지금도 유효하다고 보기에는 오래되었습니다.`,
    neverRecorded: (field: string) => `${field} 은(는) 한 번도 기록된 적이 없습니다.`,
    flagAria: (quality: string) => `데이터 품질: ${quality.toLowerCase()}`,
    noneReported: '이 레코드에는 필드별 품질 정보가 없습니다.',
    allConfirmed: (total: number) => `${total}개 필드 모두 확인됨.`,
    unconfirmedCount: (unconfirmed: number, total: number) =>
      `${total}개 필드 중 ${unconfirmed}개 미확인`,
  },

  freshness: {
    /**
     * 다섯 개의 시계는 각각 다른 주기로 갱신되므로 절대 하나로 합치지 않는다 (§64).
     */
    sources: {
      profile: '프로필',
      kills: '처치 수',
      standings: '순위',
      heroGear: '영웅 장비',
      allianceRoster: '연맹 명단',
      alliance: '연맹',
      kingdom: '킹덤',
    },
    stripLabel: '소스별 데이터 최신성',
    never: '없음',
    buckets: {
      FRESH: '이 소스의 정상 갱신 주기 이내',
      RECENT: '정상 갱신 주기보다 조금 늦음',
      AGING: '눈에 띄게 늦음. 파생 수치는 주의해서 볼 것',
      STALE: '판단 근거로 삼기에는 너무 오래됨',
      UNKNOWN: '한 번도 성공적으로 가져오지 못함',
    },
    badgeTitle: (label: string, age: string, explanation: string) =>
      `${label}: ${age} 경과 — ${explanation}`,
  },

  inference: {
    confidenceTitleWith: (percent: string) =>
      `모델 신뢰도 ${percent}. 관측값이 아니라 계산된 추정값입니다.`,
    confidenceTitle: '관측값이 아니라 계산된 추정값입니다.',
    calculated: '추론값',
    calculatedTitleWith: (modelVersion: string) =>
      `게임이 보고한 값이 아니라 이 시스템이 계산한 추정값입니다 (${modelVersion}).`,
    calculatedTitle: '게임이 보고한 값이 아니라 이 시스템이 계산한 추정값입니다.',
    nouns: {
      samples: '샘플',
      snapshots: '스냅샷',
      sessions: '세션',
      rallies: 'Rally',
      recordedRallies: '기록된 Rally',
      observedRallies: '관측된 Rally',
      leads: '리드',
      joins: '참여',
      observations: '관측',
    },
    noSamples: (noun: string) => `${noun} 없음`,
    noSamplesTitle: (noun: string) => `기록된 ${noun}이(가) 없어 평균을 낼 대상이 없습니다.`,
    lowSamples: (samples: number, noun: string) => `${noun} ${samples} · 부족`,
    lowSamplesTitle: (samples: number, noun: string, threshold: number) =>
      `${noun}이(가) ${samples}뿐입니다. ${threshold} 미만에서는 편차가 지금 보고 있는 차이보다 대개 더 큽니다 — 측정값이 아니라 참고 단서로 읽으세요.`,
    samples: (samples: number, noun: string) => `${noun} ${samples}`,
    samplesTitle: (samples: number, noun: string) => `이 수치의 근거는 ${noun} ${samples}건입니다.`,
    noComponents:
      '이 점수는 구성 요소를 기록하지 않아 설명할 수 없습니다. 검증되지 않은 값으로 취급하세요.',
    whyThisScore: '이 점수의 근거',
    noReasons: '모델이 이 결과를 낸 이유를 기록하지 않았습니다.',
  },

  overview: {
    title: '개요',
    subtitle: '기준 연맹의 현재 상태와, 다음 작전 전에 확인이 필요한 사항입니다.',
    loading: '개요 불러오는 중',
    noCoreAllianceBody:
      '아직 아무것도 동기화되지 않았습니다. R5가 서버 설정에서 킹덤과 기준 연맹을 지정하고 첫 동기화를 실행해야 하며, 이 화면은 그 시점부터 채워집니다.',
    alliancePower: '연맹 전투력',
    stateRank: '스테이트 순위',
    stateRankFoot: '킹덤 내 연맹 전투력 기준',
    stateRankAbsent: '아직 순위 없음',
    stateRankValue: (rank: number) => `${rank}위`,
    memberCount: (count: string, max: string | null) => (max ? `${count} / ${max}` : count),
    growth24h: '24시간 성장',
    growth7d: '7일 성장',
    moversTitle: '변동이 큰 멤버',
    moversEmpty:
      '스냅샷 사이에 기록에 잡힐 만큼 변화한 멤버가 아직 없습니다. 하루 이상 간격의 스냅샷이 두 개 이상 쌓이면 채워집니다.',
    recentActivityTitle: '최근 활동',
    recentActivityEmpty:
      '아직 감지된 항목이 없습니다. 가입, 탈퇴, 닉네임 변경, TC 업그레이드는 연속된 두 번의 동기화를 비교할 수 있게 되면 여기에 나타납니다.',
    detectedBadge: '감지됨',
    detectedTitle:
      '스냅샷 비교로 감지한 결과입니다. 동기화를 한 번 놓치면 오탐이 생길 수 있습니다.',
    upcomingTitle: '예정된 작전',
    upcomingEmpty:
      '예정된 작전이 없습니다. 작전은 R4가 직접 생성하며, 게임에서 추론하지 않습니다.',
    columns: {
      operation: '작전',
      name: '이름',
      starts: '시작',
      status: '상태',
    },
    noStartTime: '시작 시각 없음',
  },

  alliance: {
    noCoreAllianceBody:
      '킹덤과 기준 연맹은 서버에서 설정하며, 최소 한 번은 동기화되어야 이 화면들에 표시할 내용이 생깁니다.',

    members: {
      title: '멤버',
      subtitle: (allianceLabel: string) =>
        `${allianceLabel} 의 명단입니다. 점선 밑줄은 상위 API가 보고했지만 확인할 수 없는 값을 뜻하며, 대개 "아직 보강되지 않음"을 의미하는 0입니다.`,
      searchPlaceholder: '멤버 검색',
      searchAria: '멤버 검색',
      emptyTitle: '동기화된 멤버 없음',
      emptyBody:
        '연맹 명단을 성공적으로 가져온 적이 한 번도 없습니다. 동기화 상태에서 최근 실패 내역을 확인하세요.',
      noSearchMatch: '검색 조건에 맞는 멤버가 없습니다.',
      /**
       * "식별한 인원"과 "게임이 보고한 인원"은 서로 다른 수치이며, 절대 하나로
       * 합치지 않는다. Jeabs는 식별되지 않은 멤버를 빈 행으로 반환하므로, 차이는
       * *우리가 보는 범위*의 공백이지 연맹을 떠난 사람이 아니다.
       */
      identified: (identified: number) =>
        `마지막 로스터 스냅샷에서 ${identified}명을 식별했습니다.`,
      identifiedOf: (identified: number, reported: number) =>
        `마지막 로스터 스냅샷에서 ${identified}명을 식별했습니다. 게임이 보고한 인원은 ${reported}명입니다.`,
      couldNotIdentify: (missing: number) => `${missing}명은 식별하지 못했습니다`,
      /**
       * 이 공백은 "줄어든다"고 명시한다. 상위 API 는 읽을 때마다 명단의 서로
       * 다른 일부만 보강해서 돌려주므로, 식별된 인원은 동기화가 쌓일수록
       * 누적된다 (실제로 한 번의 명단 조회에서 46명 → 55명). 고정된 한계처럼
       * 읽히면 운영자가 연맹 절반을 영영 못 본다고 오해한다.
       */
      couldNotIdentifyWhy:
        '— 이번 조회에서 데이터 소스가 이름이나 ID 없이 반환했기 때문입니다. 연맹을 떠난 것이 아닙니다. 조회할 때마다 보강되는 대상이 달라지므로, 동기화가 쌓이면 이 목록은 점점 채워집니다.',
    },

    growth: {
      title: '성장',
      subtitle: (allianceLabel: string) => `${allianceLabel} 의 전투력 및 인원 추이입니다.`,
      loading: '성장 정보 불러오는 중',
      cardTitle: '성장',
      window24h: '24시간',
      window7d: '7일',
      window30d: '30일',
      memberChange7d: '7일 인원 변화',
      thinHistory: (snapshots: number) =>
        `스냅샷 ${snapshots}개로 산출했습니다. 한 시점이 아니라 추세를 설명하려면 이보다 더 많은 이력이 필요합니다.`,
      emptyTitle: '아직 성장 분석 없음',
      emptyBody:
        '연맹 성장은 연속된 스냅샷에서 산출합니다. 스냅샷이 두 개 이상 쌓이면 변화량이 여기에 표시됩니다.',
    },

    activity: {
      title: '활동',
      subtitle: '연속된 스냅샷 비교로 감지한 가입, 탈퇴, 닉네임 변경, 업그레이드입니다.',
      loading: '활동 불러오는 중',
      emptyTitle: '아직 감지된 항목 없음',
      emptyBody:
        '감지는 직전 스냅샷과 비교하는 방식이므로, 두 번째 동기화가 성공한 뒤부터 항목이 나타납니다.',
      caveat:
        '이 항목들은 게임의 알림이 아니라 스냅샷 차이에서 끌어낸 추론입니다. 동기화를 한 번 놓치면 한 번의 이동이 두 번처럼 보일 수 있습니다.',
      columns: {
        detected: '감지 시각',
        event: '이벤트',
        player: '플레이어',
        alliance: '연맹',
        relatedAlliance: '관련 연맹',
        confidence: '신뢰도',
      },
    },

    roles: {
      title: '역할',
      subtitle: '누가 어떤 역할을 맡는지, 그리고 추천 엔진을 덮어쓰는 수동 결정입니다.',
      feature: '역할 배정',
      phase: '수동 운영 오버라이드를 제공하는 단계 (스펙 §51)',
      describes:
        '이 화면에는 리드 고정, 리드 제외, 수비대 우선, 참여 불가 같은 상시 결정이 담깁니다. 이 결정들은 앱의 모든 점수보다 우선합니다. 오버라이드 API가 생기기 전까지는 Bear 플래너에서 계획 단위로 지정할 수 있으며, 제약 조건으로 전달됩니다.',
    },
  },

  player: {
    loading: '플레이어 불러오는 중',
    tabs: {
      growth: '성장',
      heroes: '영웅',
      capabilities: '역량',
      bear: 'Bear',
      events: '작전 기록',
    },
    columns: {
      profileAge: '프로필 경과',
    },
  },

  growth: {
    metricsEmptyTitle: '아직 성장 분석 없음',
    metricsEmptyBody:
      '성장은 스냅샷이 최소 두 개 있어야 산출합니다. 이 플레이어가 두 번 이상 동기화되면 24시간 / 7일 / 30일 수치가 샘플 수와 함께 표시됩니다.',
    cardTitle: '성장',
    percentileAlliance: '연맹 내 위치',
    percentileKingdom: '킹덤 내 위치',
    growthFlag: '성장 분류',
    growthFlagTitle: '이 시스템이 부여한 분류이며, 게임이 보고하는 값이 아닙니다.',
    historyTitle: '전투력 이력',
    historyLoading: '이력 불러오는 중',
    historyEmptyTitle: '이 기간에 스냅샷 없음',
    historyEmptyBody:
      '스냅샷은 플레이어 데이터가 실제로 바뀌었을 때만 저장하므로, 조용한 계정은 스냅샷이 없는 것이 정상일 수 있습니다. 기간을 넓혀 보거나, 활동이 있었다고 판단되면 동기화 상태를 확인하세요.',
    snapshotLogTitle: '스냅샷 로그',
    snapshotLogEmpty: '이 기간에 기록된 내용이 없습니다.',
    notEnoughPointsTitle: '선을 그릴 만큼의 점이 없음',
    notEnoughPointsBody: (points: number) =>
      `이 기간에 전투력이 기록된 스냅샷은 ${points}개뿐입니다. 추세를 그리려면 최소 두 개가 필요합니다.`,
    omittedPoints: (omitted: number) =>
      `이 기간의 스냅샷 ${omitted}개는 전투력이 기록되어 있지 않아, 0으로 그리는 대신 선에서 제외했습니다.`,
    columns: {
      captured: '수집 시각',
    },
  },

  heroes: {
    loading: '영웅 정보 불러오는 중',
    emptyTitle: '동기화된 영웅 데이터 없음',
    emptyBody:
      '영웅과 장비 데이터는 별도의 상위 요청에서 각자의 주기로 들어옵니다. 이 플레이어에 대해 한 번도 성공한 적이 없다면 저장된 내용이 없습니다.',
    noHeroesTitle: '최신 스냅샷에 영웅 없음',
    noHeroesBody: '스냅샷은 있지만 영웅 항목이 들어 있지 않습니다.',
    rosterBadge: (completeness: string) => `목록 상태: ${completeness}`,
    rosterCompleteness: {
      UNVERIFIED: '미검증',
      ASSUMED_COMPLETE: '완전하다고 가정',
      KNOWN_PARTIAL: '일부만 확인됨',
    },
    rosterNote: {
      UNVERIFIED:
        '상위 API의 영웅 목록이 완전한 보유 목록이라는 근거가 없습니다. 이 플레이어가 가진 영웅이 목록에서 빠져 있을 수 있으므로, 여기 없다는 것이 게임에도 없다는 증거는 아닙니다.',
      ASSUMED_COMPLETE:
        '이 목록은 완전하다고 가정하고 있지만, 게임 클라이언트와 대조해 검증한 적은 없습니다.',
      KNOWN_PARTIAL:
        '이 목록은 일부만 담고 있는 것으로 확인되었습니다. 그들이 보유한 영웅이 아니라, 우리가 볼 수 있는 영웅으로 읽으세요.',
    },
    rawHeroId: (heroId: number | string) => `영웅 #${heroId}`,
    rawHeroIdTitle:
      '이 영웅 ID는 카탈로그에 없습니다. 이름을 추측하는 대신 원본 ID를 그대로 표시합니다.',
    unverifiedId: '미검증 ID',
    unverifiedIdTitle: 'hero_catalog 에 없거나, 있더라도 검증되지 않았습니다.',
    unverified: '미검증',
    unverifiedEquipmentTitle: 'equipment_catalog 에 없습니다.',
    level: '레벨',
    notReported: '보고되지 않음',
    /** Jeabs 의 `star` 값. 인코딩이 검증되지 않아 별 개수로 그리지 않는다. */
    starStage: '스타 단계',
    starStageTitle:
      'Jeabs 는 이 필드를 `star` 라고 부릅니다. 인코딩이 검증되지 않았으므로 단계 숫자로만 표시하며, 별 개수로 그리지 않습니다.',
    slotPosition: '슬롯 / 위치',
    exclusiveEquipment: '전용 장비',
    equipLevel: (level: number) => ` · ${level}레벨`,
    noneRecorded: '기록된 항목 없음',
    skills: '스킬',
    noSkills: '이 스냅샷에 스킬 항목이 없습니다.',
    rawSkillId: (skillId: number | string) => `스킬 #${skillId}`,
    skillLevel: (level: number) => `${level}레벨`,
    skillLevelUnknown: '레벨 알 수 없음',
    skillOrderNote:
      '순서는 표시 순서일 뿐이며, 스킬 우선순위에 대해 검증된 의미를 갖지 않습니다.',
    equipmentBySlot: '슬롯별 장비',
    noEquipment: '이 스냅샷에 장비 항목이 없습니다.',
    equipmentColumns: {
      slotId: '슬롯 ID',
      slotIdTitle:
        '상위 API의 `sid` 값입니다. 슬롯 구분은 이 값에서 오며, 항목이 도착한 순서에서 오지 않습니다.',
      equipment: '장비',
      enhance: '강화',
      refine: '연마',
    },
    opaqueTitle: '상위 API 원본 스탯',
    opaqueBlurb:
      'Jeabs 에서 의미가 문서화되지 않은 번호 키로 들어오는 값입니다. 받은 그대로 표시합니다. 카탈로그에 그 키가 무엇인지에 대한 추정이 있으면 신뢰도와 함께 후보로 표시합니다 — 후보는 이름이 아니며, 검증되지 않은 키로는 이 앱의 어떤 계산도 수행하지 않습니다.',
    opaqueEmpty: '최신 스냅샷에 스탯 키가 없습니다.',
    opaqueColumns: {
      key: '키',
      value: '값',
      candidateMeaning: '후보 의미',
      candidateConfidence: '후보 신뢰도',
    },
    verified: '검증됨',
    candidateOnly: '후보일 뿐',
    noCandidate: '후보 없음',
  },

  capabilities: {
    loading: '역량 불러오는 중',
    emptyTitle: '아직 역량 점수 없음',
    emptyBody:
      '역량 점수는 기록된 작전과 플레이어 상태로부터 다시 계산합니다. 이 연맹이 작전을 실제로 수행하고 기록하기 전까지는 점수를 낼 근거가 없으며, 근거 없이 만든 점수는 전투력을 다른 이름으로 부른 것에 지나지 않습니다.',
    blurb: '— 아래 수치는 모두 이 시스템이 계산한 값이며, 게임이 보고한 값이 아닙니다.',
    computedFrom: (when: string) => ` ${when} 기준 데이터로 산출했습니다.`,
    whatThisScoreIsMadeOf: '이 점수의 구성 요소',
  },

  eventHistory: {
    /** §50: 인원 배치를 위한 기록이며 서열화가 아니다. */
    notice:
      '참여 기록은 작전 인원을 배치하기 위한 것이며, 구성원을 서열화하기 위한 것이 아닙니다. 이 목록에 없다는 것은 기록된 내용이 없다는 뜻일 뿐, 해당 플레이어에 대한 평가가 아닙니다.',
    bearTitle: 'Bear Trap',
    bearBody: 'Bear 참여 내역과 Rally별 결과는',
    bearTabName: 'Bear',
    bearBodyTail: '탭에 있으며, 동일한 기록 세션으로 만들어집니다.',
    otherTitle: '기타 작전',
    otherBody:
      '이 작전들을 기록하는 화면이 아직 만들어지지 않아 결과가 기록된 적이 없습니다. 화면이 생기면 각 작전이 해당 플레이어의 역할과 기여와 함께 여기에 표시됩니다.',
    seeEvents: '각 항목이 다룰 내용은',
    seeEventsLink: '작전',
    seeEventsTail: '에서 확인하세요.',
  },

  bearAnalysis: {
    myTitle: '내 Bear 분석',
    playerTitle: 'Bear 분석',
    loading: 'Bear 분석 불러오는 중',
    resolvingAccount: '계정 확인 중',
    openFullPage: '전체 화면으로 열기',
    openPlayerDetail: '플레이어 상세 보기',
    openPlayerDetailInline: '플레이어 상세 보기',
    noPlayerTitle: '선택된 플레이어 없음',
    noPlayerBody: '이 경로에는 플레이어 ID가 필요합니다.',
    notLinkedTitle: '계정에 아직 플레이어가 연결되어 있지 않습니다',
    notLinkedBody:
      '이 화면은 본인의 기록된 Bear Rally를 분석하므로, 어떤 인게임 계정이 본인인지 알아야 합니다. 연결은 수동 작업이며, R5가 워크스페이스 계정에 플레이어를 지정합니다. 잘못된 것도, 누락된 데이터도 없습니다.',
    meantimeTitle: '그동안에는',
    meantimeBody: '기록된 Rally가 있는 사람이라면 누구든 같은 분석을 볼 수 있습니다.',
    browseMembers: '멤버 둘러보기',
    openAnalysisFor: 'Bear 분석을 열 플레이어',
    selectPlayer: '플레이어 선택…',

    /** 분석 결과가 0이라는 뜻이 아니라, 기록된 내용이 없다는 뜻이다. */
    noParticipations: '이 플레이어의 Bear 참여 기록이 아직 없습니다.',
    emptyTitle: '이 플레이어에 대해 기록된 Bear 세션이 없습니다',
    emptyBody:
      '이 분석은 전적으로 R4가 세션 후에 입력한 Rally 기록만으로 만들어집니다. 전투력이나 장비만으로는 아무것도 추론하지 않습니다. 장비는 Bear 피해량을 잘 설명하지 못하며, 그것으로 지어낸 수치는 실제 수치와 똑같이 자신 있어 보이기 때문입니다.',

    recommendationTitle: '현재 권장 사항',
    /**
     * 권장이지 단정이 아니다. 어느 항목도 플레이어가 "무엇이다"라고 말하지 않는다.
     */
    roleHeadline: {
      LEAD: 'Rally를 여는 쪽이 더 나아 보임',
      JOIN: 'Rally에 참여하는 쪽이 더 나아 보임',
      MIXED: '웨이브에 따라 양쪽 모두',
      INSUFFICIENT_DATA: '판단하기에는 기록된 세션이 부족함',
    },
    leadAverage: '리드 평균',
    neverLed: '리드한 적 없음',
    joinAverage: '참여 평균',
    neverJoined: '참여한 적 없음',
    leadVsJoin: '리드 대비 참여',
    leadVsJoinFoot: '양수면 리드가 유리',
    notComparable: '비교 불가',
    returnCycle: 'Rally 복귀 주기',
    returnCycleFoot: '관측 중앙값',
    insufficientNotice:
      '기록된 이력이 너무 적어 어느 역할이 더 낫다고 보기 어렵습니다. 양쪽 역할 모두에서 세션을 몇 번 더 기록한 뒤에 이 값을 기준으로 계획하세요.',
    computedFrom: (when: string) => `${when} 기준 데이터로 산출했습니다.`,
    unrecordedTime: '기록되지 않은 시각',

    leadPerformance: '리드 성과',
    joinPerformance: '참여 성과',
    noRalliesInRole: '이 역할로 기록된 Rally가 없습니다.',
    averageDamage: '평균 피해량',
    medianDamage: '중앙값 피해량',
    bestRecorded: '기록된 최고치',
    damagePerTroop: '병력당 피해량',
    damagePerTroopTitle:
      '병력 수가 실제로 입력된 Rally만으로 산출합니다. 병력 칸이 비어 있는 Rally는 0으로 세지 않고 계산에서 제외합니다.',
    noTroopCounts: '기록된 병력 수 없음',
    thinRoleSamples: (samples: number) =>
      `Rally ${samples}건은 평균이 의미를 갖기에는 부족합니다. 개별 Bear Rally 사이의 편차는 대개 지금 읽어내려는 차이보다 큽니다.`,

    bestJoinTargets: '가장 잘 맞는 참여 대상',
    bestJoinTargetsBlurb:
      '이 플레이어가 가장 큰 피해량을 낸 Rally의 리드들입니다. 리드 자체의 실력이 아니라 두 사람의 조합을 나타냅니다.',
    rallyLead: 'Rally 리드',
    noJoinTargets: '이름이 확인된 리드의 Rally에 참여한 기록이 아직 없습니다.',
    byCaptainHero: '대장 영웅별',
    captainHero: '대장 영웅',
    noCaptainHero: '이 플레이어의 Rally에 대장 영웅이 기록된 적이 아직 없습니다.',
    formationComparison: '편성 비교',
    formationBlurb:
      '이 플레이어 자신의 기준선으로 정규화했으므로, 연맹 전체가 아니라 편성끼리 비교합니다.',
    formation: '편성',
    noFormation: '이 플레이어의 Rally에 병력 편성이 기록된 적이 아직 없습니다.',
    byWave: '웨이브별',
    noWaves: '기록된 웨이브 번호가 아직 없습니다.',

    breakdownColumns: {
      rank: '#',
      mean: '평균',
      median: '중앙값',
      best: '최고',
      normalized: '정규화',
    },
    normalizedValue: (value: string) => `${value}배`,
    thinRows: (thin: number, total: number, threshold: number) =>
      `${total}개 행 중 ${thin}개는 샘플이 ${threshold}개 미만입니다. 해당 행들 사이의 순서는 신뢰할 수 없습니다.`,

    trendsTitle: '최근 추이',
    trendWindows: {
      last3: '최근 3세션',
      last5: '최근 5세션',
      last10: '최근 10세션',
    },
    trendColumns: {
      window: '구간',
      meanDamage: '평균 피해량',
      medianDamage: '중앙값 피해량',
      changeVsPrevious: '직전 구간 대비 변화',
      sessions: '세션',
    },
    noEarlierWindow: '이전 구간 없음',
    trendsCaveat:
      '구간 사이의 변화에는 그 사이에 달라진 모든 것이 섞여 있습니다 — 상대 트랩 레벨, 누가 함께 참여했는지, 웨이브 위치, 플레이어 본인의 계정 상태까지. 이는 무슨 일이 있었는지에 대한 서술이지, 향상 정도를 재는 지표가 아닙니다.',
  },

  /**
   * 업그레이드 영향 (스펙 §27).
   *
   * 여기의 표현은 스타일이 아니라 규정이다: `~와 연관됨`, `~ 이후 관측됨`,
   * `기여 가능성`, `통제된 샘플 부족`. 어떤 문장도 변경이 차이를 "만들었다"고
   * 말하지 않으며, `~때문에`·`~로 인해` 는 쓰지 않는다.
   */
  upgradeImpact: {
    title: '업그레이드 영향',
    blurb:
      '기록된 변경 전후로 관측된 피해량입니다. 실제 세션에서 얻은 통제되지 않은 관측이며 실험이 아닙니다 — 측정된 효과가 아니라 검증해 볼 단서로 읽으세요. 변경 하나를 충분히 오래 고정해 두고 더 확실한 말을 할 수 있는 곳은 실험 화면입니다.',
    empty:
      '이 플레이어의 Bear 이력에 대해 기록된 변경이 아직 없습니다. 영웅·스킬·장비 변경이 기록되고 그 앞뒤로 세션이 쌓이면 여기에 표시됩니다.',
    recordedAt: (when: string) => `${when} 기록`,
    notIsolated: '단독 변경 아님',
    notIsolatedTitle: '같은 구간에 다른 변경도 함께 있었습니다.',
    medianBefore: '변경 전 중앙값',
    medianAfter: '변경 후 중앙값',
    differenceObserved: '변경 이후 관측된 차이',
    notEnoughData: '데이터 부족',
    alsoChanged: '같은 구간에 함께 변경된 항목:',

    /** 교란 있음: 최대한으로도 "기여 가능성"까지만. 귀속시키지 않는다. */
    confounded: (description: string, others: number) =>
      `이 차이는 "${description}" 이(가) 기록된 구간과 연관됨. 다만 같은 구간에 다른 변경 ${others}건이 함께 발생했습니다. 이 근거만으로 "${description}" 은(는) 기여 가능성이 있는 요인일 뿐이며, 차이를 여기에 귀속시킬 수 없습니다. 이를 분리하려면 다른 변수를 고정한 실험이 필요합니다.`,
    /** 한쪽 표본이 얇아 통상적인 변동과 구분되지 않음. */
    thin: (description: string, side: string) =>
      `"${description}" 기록 이후 관측됨. ${side}에 통제된 샘플 부족으로, 세션마다 생기는 통상적인 변동과 구분할 수 없습니다. 결과가 아니라 확인해 볼 단서로 다루세요.`,
    beforeSide: '변경 전 쪽',
    afterSide: '변경 후 쪽',
    /** 가장 강하게 말할 수 있는 수준: 기여 가능성. 여전히 통제되지 않은 관측이다. */
    clean: (description: string) =>
      `"${description}" 기록 이후 관측됨. 같은 구간에 기록된 다른 변경은 없습니다. 그만큼 이 차이에 대한 기여 가능성이 있는 요인입니다. 다만 여전히 통제되지 않은 관측입니다 — 상대, 웨이브, 참여자는 세션마다 달랐습니다.`,
  },

  planner: {
    title: '연맹 Bear 플래너',
    noCoreAllianceBody:
      '플래너는 기준 연맹 명단으로 동작합니다. 먼저 연맹을 설정하고 동기화하세요.',
    noRosterTitle: '동기화된 명단 없음',
    noRosterBody: '연맹 명단을 아직 가져오지 않아 계획에 넣을 인원이 없습니다.',

    objective: '목표',
    modes: {
      MAX_ALLIANCE_DAMAGE: {
        label: '연맹 피해량 최대화',
        blurb:
          '관측 성과가 가장 좋은 리드를 앞 웨이브에 배치합니다. 트랩을 확실히 잡으려 할 때 적합합니다.',
      },
      BALANCED_REWARDS: {
        label: '보상 분배 균형',
        blurb:
          '리드 자리를 분산해 더 많은 멤버가 리드 보상을 받게 합니다. 총 피해량은 다소 줄어듭니다.',
      },
      PERSONAL_SCORE_ASSIST: {
        label: '한 명 지원',
        blurb: '한 멤버를 중심으로 웨이브와 참여를 배치합니다. 아래에서 대상을 선택하세요.',
      },
    },
    waves: '웨이브 수',
    ralliesPerWave: '웨이브당 Rally 수',
    autoPlaceholder: '자동',
    generating: '생성 중…',
    notPersisted:
      '이 계획은 저장되지 않아 감사 기록이 없으며, 나중에 수정하거나 다시 불러올 수 없습니다. 실행할 내용이 있다면 이 화면을 떠나기 전에 처리하세요.',

    change: '변경',
    creating: '여는 중…',
    useIt: '열기',

    filterRoster: '명단 필터',
    leadDecision: '리드 결정',
    leadDecisionAria: (name: string) => `${name} 의 리드 결정`,
    planAround: '중심 대상',
    leadPreference: {
      AUTO: '플래너가 결정',
      FORCE_LEAD: '리드로 고정',
      DO_NOT_LEAD: '오늘은 리드 안 함',
    },
    noFilterMatch: '필터 조건에 맞는 멤버가 없습니다.',
  },

  /**
   * Bear Trap 하루 단위 화면.
   *
   * BT1/BT2 배타는 게임 규칙이고(참여 시 약 48시간 쿨다운, 두 트랩은 같은 날),
   * 웨이브 수와 리드 수는 이 연맹의 관행이다. 둘을 뭉뚱그려 번역하면 바꿀 수
   * 있는 관행이 바꿀 수 없는 규칙처럼 읽힌다.
   */
  bearDay: {
    subtitle:
      'Bear Trap 하루를 계획합니다. 두 트랩은 같은 날에 진행되고, 한쪽에 참여하면 약 48시간 쿨다운이 걸립니다. 그래서 멤버는 BT1, BT2, 미참여 중 하나입니다.',

    dayTitle: 'Bear Trap 날짜',
    dayBlurb:
      '날짜를 고르세요. 연맹당 하루에 하나이며 두 트랩을 모두 포함합니다 — BT1/BT2 선택이 권장이 아니라 배타가 되는 근거입니다.',
    date: '날짜',
    openDay: '이 날짜 열기',
    recentDays: '최근 날짜',
    loadingDays: 'Bear Trap 날짜 불러오는 중',
    loadingDay: '해당 날짜 불러오는 중',
    noDaysYet: '아직 Bear Trap 날짜가 없습니다. 위에서 하나 열어주세요.',
    ralliesEntered: '입력된 Rally',
    trapAssignedCount: (trapNumber: number, count: number) =>
      `BT${trapNumber}: ${count}명 배정`,

    assignmentTitle: (bt1: number, bt2: number) => `배정 — BT1 ${bt1}명 · BT2 ${bt2}명`,
    assignmentBlurb:
      '멤버를 한 트랩에 배정하거나 미참여로 두세요. 두 트랩에 모두 넣을 수는 없습니다 — 참여 시 약 48시간 쿨다운이 걸리고 두 트랩은 같은 날에 진행되므로, 연맹 방침이 아니라 게임이 막는 것입니다. 이미 정해둔 리드는 고정하거나 제외하면 됩니다 — 수동 결정은 플래너의 어떤 추천보다도 우선합니다.',
    trapColumn: '트랩',
    neither: '미참여',
    trapChoiceAria: (name: string, choice: string) => `${name}: ${choice}`,
    splitEvenly: '표시된 인원 반반 나누기',
    saveAssignment: '배정 저장',
    saving: '저장 중…',
    unsavedChanges: '저장되지 않은 변경',
    savedAt: (bt1: number, bt2: number) => `저장됨. BT1 ${bt1}명, BT2 ${bt2}명.`,
    unassignedCount: (count: number) => `미참여 ${count}명`,

    trapTitle: (trapNumber: number, count: number) =>
      `Bear Trap ${trapNumber} — ${count}명 배정`,
    trapEmpty: (trapNumber: number) =>
      `트랩 ${trapNumber}에 배정된 인원이 아직 없습니다. 위에서 배정하고 저장하면 이 트랩의 규모와 계획을 산출할 수 있습니다.`,
    loadingSizing: '이 트랩을 얼마나 크게 운영할지 계산 중',
    enterRallies: '이 트랩의 Rally 입력',

    recommendedLeads: 'Rally 리드 수',
    totalLaunches: '총 Rally 발진 수',
    /** 행에서 직접 센 값. 규칙에 적힌 숫자보다 강한 증거다. */
    observedCounted: (count: number, detail: string) =>
      `${detail}의 기록에서 Rally 리드 ${count}명을 직접 셌습니다.`,
    observedQuoted: (count: number) =>
      `전략 규칙에 기록된 Rally 리드 수는 ${count}명입니다. 아직 입력된 트랩에서 직접 센 값은 없습니다.`,
    sizingConfidence: (percent: number) =>
      `신뢰도 ${percent}% — 읽지 못하고 가정해야 했던 값만큼 낮아집니다.`,

    useRecommendation: '추천대로 진행',
    decideMyself: '웨이브와 Rally 수를 직접 지정',
    blankMeansAuto: '비워두면 해당 값은 플래너가 결정합니다.',
    pickFocus: '중심이 될 멤버를 선택하세요',
    generateForTrap: (trapNumber: number) => `BT${trapNumber} 계획 생성`,
  },

  planResult: {
    wavePlan: '웨이브 계획',
    rules: (version: string) => `규칙 ${version}`,
    noWaves: '플래너가 웨이브를 만들지 못했습니다. 아래 경고를 확인하세요.',
    waveHeading: (wave: number) => `웨이브 ${wave}`,
    noRalliesInWave: '이 웨이브에 배정된 Rally가 없습니다.',
    opensAt: (when: string) => `${when} 개시`,
    relativeStrength: (value: string) => `상대 ${value}배`,
    relativeStrengthTitle:
      '이 계획에 포함된 다른 Rally 대비 값입니다. 절대 피해량 예측이 아닙니다.',
    joiners: '참여자:',
    noJoinerPreference: '참여자 선호 없음 — 근거로 삼을 조합 기록이 없습니다.',
    backupLead: (name: string) => `예비 리드: ${name}`,
    noBackupLead: '가용한 예비 리드가 없습니다.',
    whyThisLead: '이 리드를 고른 근거',

    assumptionsTitle: '이 계획이 전제하고 있는 가정',
    noAssumptions:
      '플래너가 가정을 하나도 기록하지 않았습니다. 계획을 실행하기 전에 이 사실 자체를 의심해 볼 만합니다.',

    leadRankingTitle: '리드 순위',
    leadRankingBlurb:
      'Bear 리드 점수일 뿐입니다. PvP, 수비대 등 다른 역할에서의 가치에 대해서는 아무것도 말하지 않습니다 — 그 역할들은 따로 점수를 매기며, 여기서 강한 리드가 다른 곳에서도 강한 경우는 오히려 드뭅니다.',
    noCandidates: '점수가 매겨진 리드 후보가 없습니다.',
    columns: {
      rank: '#',
      leadScore: '리드 점수',
      observedAvgDamage: '관측 평균 피해량',
      wave: '웨이브',
      explain: '설명',
    },
    neverLed: '리드한 적 없음',
    breakdown: '구성 요소',

    planConfidence: '계획 신뢰도',
    planConfidenceFoot: '전체 웨이브 기준',
    storedAs: '저장 상태',
    recorded: '기록됨',
    notPersistedFoot: '저장되지 않음 — 이 계획에는 감사 기록이 없습니다',
  },

  persistedPlan: {
    loading: '저장된 계획 불러오는 중',
    title: '저장된 계획',
    manuallyEdited: '수동 수정됨',
    manuallyEditedTitle:
      '운영자가 이 계획을 수정했습니다. 지금 보고 있는 것은 엔진의 것이 아니라 운영자의 버전입니다.',
    asGenerated: '생성된 그대로',
    hideOriginal: '원본 숨기기',
    compareOriginal: '원본과 비교',
    stopEditing: '편집 중단',
    editPlan: '이 계획 편집',
    model: '모델',
    rules: '규칙',
    generated: '생성 시각',
    edited: '수정 시각',
    editedBy: (who: string) => ` · ${who}`,
    reason: '사유',
    manualNotice:
      '이 계획은 생성된 뒤 운영자가 변경했습니다. 연맹이 따라야 할 것은 운영자의 버전입니다. 엔진의 원본은 비교용으로 보관되며 이 버전을 덮어쓰지 않습니다 — 다시 생성하면 교체가 아니라 새 계획이 만들어집니다.',
    effectiveOperator: '실제 적용 계획 (운영자 버전)',
    effective: '실제 적용 계획',
    comparisonNotice: '아래는 수정 전 엔진이 만든 결과입니다. 비교용으로만 표시합니다.',
    originalAsGenerated: '원본, 생성된 그대로',
    legacyTitle: (label: string) => `${label} — 이전 계획 형식으로 작성됨`,
    legacyBodyPrefix: '이 계획은 ',
    legacyBodySuffix:
      ' 이(가) 생성했으며, 그 출력은 현재 계획 스키마와 더 이상 맞지 않습니다. 재해석하지 않고 원본 그대로 보관합니다. 옛 형식이 새 형식에 어떻게 대응하는지 추측하면 원본에 없던 세부 사항을 지어내게 되기 때문입니다.',
    legacyNotEditable:
      '이 계획은 이전 형식으로 작성되어 그 자리에서 편집할 수 없습니다. 새 계획을 생성하세요 — 어느 쪽이든 기존 계획은 기록에 그대로 남습니다.',
    editBlurb:
      'Rally 리드를 재배정합니다. 운영자의 버전은 엔진의 것을 덮어쓰지 않고 나란히 저장되므로, 나중에 재계산이 일어나도 의도적으로 내린 결정이 조용히 되돌려지지 않습니다.',
    noRalliesInWave: '이 웨이브에 Rally가 없습니다.',
    rallyLabel: (index: number) => `Rally ${index}`,
    notePlaceholder: '예: Damned 가 오늘 접속하지 않아 Sable 로 교체',
    noteBlurb:
      '사유를 남기는 일은 보기보다 중요합니다. 3주 뒤에 계획과 엔진이 왜 다른지 설명해 주는 것은 이것뿐입니다.',
    saveMyVersion: '내 버전 저장',
    manualExplanation: '운영자가 엔진 결정을 덮어쓰고 직접 배정했습니다.',
  },

  bearHistory: {
    title: 'Bear Trap 기록',
    subtitle:
      '실제로 무슨 일이 있었는지 기록하세요. 이 앱의 모든 분석은 오직 이 입력만으로 만들어집니다.',
    noCoreAllianceBody:
      '세션은 연맹에 속하므로, 연맹을 먼저 설정하고 동기화해야 합니다.',
    session: '세션',
    recordingInto: '기록 대상',
    savedThisSitting: (count: number) => `이번에 Rally ${count}건 저장됨`,
    closeSession: '세션 닫기',
    trapNumber: '트랩 번호',
    startSession: '세션 시작',
    starting: '시작 중…',
    orContinue: '또는 기존 세션 이어서',
    sessionIdPlaceholder: '세션 ID',
    existingSessionIdAria: '기존 세션 ID',
    continue: '이어서',
    startToRecord: 'Rally를 기록하려면 세션을 시작하거나 이어서 여세요.',
    noRosterTitle: '동기화된 명단 없음',
    noRosterBody: 'Rally 참여자는 연맹 명단에서 고르는데, 아직 명단을 가져오지 않았습니다.',

    pastSessions: '지난 세션',
    loadingSessions: '세션 불러오는 중',
    noSessionsTitle: '아직 기록된 세션 없음',
    noSessionsBody:
      '이 앱의 모든 Bear 분석은 여기에 입력한 세션으로 만들어집니다. 첫 세션이 기록되면 이 목록에 나타납니다.',
    draftsNotice: (drafts: number) =>
      `세션 ${drafts}건이 아직 입력 중입니다. 해당 세션의 합계는 부분 합계이며, 입력이 끝날 때까지 거기서 끌어낸 분석은 불완전한 데이터로 작업하는 셈입니다.`,
    columns: {
      session: '세션',
      started: '시작',
      entry: '입력',
      coverage: '입력률',
      rallies: 'Rally',
      allianceDamage: '연맹 피해량',
    },
    noTrapNumber: '트랩 번호 없음',
    trapLabel: (trapNumber: number) => `Bear Trap #${trapNumber}`,
    partialTitle: '입력이 끝나지 않아, 이 합계는 지금까지 입력된 분량만 포함합니다.',
    partialSuffix: '부분 합계',
    continueEntry: '입력 이어서',
    enteringNow: '지금 입력 중',
    continueEntryTitle: '위 입력 폼에서 이 세션을 다시 열어 계속 기록합니다.',
    showingRecent: (shown: number, total: string) =>
      `기록된 세션 ${total}건 중 최근 ${shown}건을 표시하고 있습니다.`,
  },

  entryProgress: {
    complete: '입력 완료',
    completeTitle: '입력이 완료로 표시되었습니다. 합계는 기록된 세션 전체입니다.',
    stillBeingEntered: '입력 중',
    stillBeingEnteredTitle:
      '아직 입력이 진행 중입니다. 이 세션의 모든 합계는 부분 합계이며, 세션의 결과로 읽어서는 안 됩니다.',
    turnoutNotEntered: '참여 인원 미입력',
    recordedOnly: (recorded: string) => `${recorded}명 기록됨 · `,
    turnoutNotEnteredTitle:
      '게임이 알려준 참여 인원이 입력되지 않아, 기록된 항목을 비교할 기준이 없습니다.',
    recordedOf: (recorded: string, expected: string) => `${expected}명 중 ${recorded}명 기록됨`,
    percentEntered: (percent: number) => `${percent}% 입력됨`,
    percentEnteredTitle: (percent: number) =>
      `실제 참여한 플레이어 중 ${percent}% 만 입력되었습니다. 이 세션의 피해량 합계는 입력된 행만 더한 값입니다.`,
    draftNoCoverage:
      '이 세션은 아직 입력 중입니다. 합계는 지금까지 기록된 분량만 더한 값이며, 세션의 결과가 아닙니다.',
    draftWithCoverage: (recorded: number, expected: number) =>
      `이 세션은 아직 입력 중입니다 — 현재까지 참여자 ${expected}명 중 ${recorded}명. 합계는 부분 합계이며, 세션의 결과가 아닙니다.`,
    completeButThin: (recorded: number, expected: number) =>
      `입력이 완료로 표시되어 있지만, 참여자 ${expected}명 중 ${recorded}명만 행이 있습니다. 아래 합계는 기록된 플레이어만 포함합니다.`,
    noTurnoutRecorded:
      '게임이 알려준 참여 인원이 입력되지 않아, 실제로 참여한 모든 플레이어에게 행이 있는지 확인할 방법이 없습니다.',
  },

  sessionDetail: {
    loading: '세션 불러오는 중',
    genericTitle: 'Bear 세션',
    started: '시작',
    ended: '종료',
    allianceTotalDamage: '연맹 총 피해량',
    asReportedByGame: '게임이 보고한 값',
    entryCoverage: '입력률',
    ralliesRecorded: '기록된 Rally 수',
    notes: '메모',
    weakContext: (weak: number, total: number) =>
      `기록된 ${total}개 행 중 ${weak}개는 연결된 영웅/장비 스냅샷이 오래되었거나 없습니다. 해당 행은 어떤 피해량이 나왔는지는 기록하지만, 어떤 세팅으로 냈는지는 신뢰할 수 없습니다 — 이 행들에서 업그레이드의 효과를 읽어내지 마세요.`,
    missingTroops: (missing: number, total: number) =>
      `${total}개 행 중 ${missing}개에 병력 수가 없습니다. 0으로 세지 않고 병력당 피해량 계산에서 제외합니다.`,
    noRalliesTitle: '이 세션에 기록된 Rally 없음',
    noRalliesBody: '세션은 있지만 아직 아무도 Rally를 입력하지 않았습니다.',
    rally: 'Rally',
    waveLabel: (wave: number) => `웨이브 ${wave}`,
    hideJoiners: '참여자 숨기기',
    showJoiners: (count: number) => `참여자 ${count}명 보기`,
    rallyDamage: 'Rally 피해량',
    troops: '병력',
    troopsOf: (actual: string, capacity: string | null) =>
      capacity ? `${actual} / ${capacity}` : actual,
    filled: (percent: number) => `${percent}% 채움`,
    returnCycle: '복귀 주기',
    leadHeroes: '리드 영웅',
    noJoinersRecorded: '이 Rally에 기록된 참여자가 없습니다.',
    columns: {
      order: '#',
      captainHero: '대장 영웅',
      infantry: '보병',
      cavalry: '기병',
      archer: '궁병',
      totalTroops: '총 병력',
      damage: '피해량',
      loadoutContext: '세팅 정합성',
      loadoutContextTitle: '연결된 영웅/장비 스냅샷이 이 Rally와 얼마나 가까운지 나타냅니다.',
    },
    /** 빈칸은 운영자가 숫자를 볼 수 없었다는 뜻이지, 0이었다는 뜻이 아니다. */
    troopNotRecorded: '미입력',
    troopNotRecordedTitle:
      '입력되지 않았습니다. 0으로 세지 않고 병력당 계산에서 제외합니다.',
    notAssessed: '판정 안 됨',
    notAssessedTitle: '서버가 세팅 스냅샷을 어떻게 연결했는지 알려주지 않았습니다.',
    contextQuality: {
      EXACT: '정확',
      NEAR: '근접',
      STALE: '오래됨',
      MISSING: '없음',
    },
    contextExplanation: {
      EXACT:
        '연결된 영웅/장비 스냅샷이 이 Rally로부터 약 30분 이내입니다. 이 행에 귀속된 세팅이 실제로 사용된 세팅일 가능성이 매우 높습니다.',
      NEAR: '연결된 스냅샷이 몇 시간 이내입니다. 대체로 맞는 세팅이겠지만, 그 사이에 변경이 있었다면 드러나지 않습니다.',
      STALE:
        '연결된 스냅샷이 이 Rally로부터 몇 시간 떨어져 있습니다. 어떤 장비나 스킬이 이 피해량을 냈는지에 대한 어떤 주장도 뒷받침할 수 없습니다.',
      MISSING:
        '이 행에는 영웅/장비 스냅샷을 전혀 연결할 수 없었습니다. 당시 이 플레이어의 세팅에 대해 알려진 것이 없습니다.',
    },
    /** @param agePhrase 방향까지 포함된 문구. 예: "3시간 20분 이전". */
    contextAge: (explanation: string, agePhrase: string) =>
      `${explanation} 연결된 스냅샷은 이 Rally보다 ${agePhrase} 시점에 수집되었습니다.`,
    before: '이전',
    after: '이후',
  },

  rallyEntry: {
    title: 'Rally 기록',
    joinersEntered: (count: number) => `참여자 ${count}명 입력됨`,
    rallyLead: 'Rally 리드',
    wave: '웨이브',
    order: '순서',
    rallyCapacity: 'Rally 정원',
    actualTroops: '실제 병력',
    totalRallyDamage: 'Rally 총 피해량',
    leadHeroIds: '리드 영웅 ID',
    leadHeroIdsPlaceholder: '예: 104 201 307',
    columns: {
      row: '#',
      joiner: '참여자',
      joinOrder: '참여 순서',
      captainHero: '대장 영웅',
      infantry: '보병',
      cavalry: '기병',
      archer: '궁병',
      totalTroops: '총 병력',
      damage: '피해량',
    },
    joinerAria: (row: number) => `${row}행 참여자`,
    cellAria: (field: string, row: number) => `${row}행 ${field}`,
    removeRowAria: (row: number) => `${row}행 삭제`,
    removeRowTitle: '행 삭제',
    /** Bear 분석 전체가 이 규칙 위에 서 있다: 빈칸은 빈칸으로 남는다. */
    legendLead: 'Enter 로 행을 추가하고 Tab 으로 이동합니다. 빈 칸은',
    legendNotRecorded: '미입력',
    legendTail:
      '으로 제출되며 절대 0으로 바뀌지 않습니다. 병력 구성을 볼 수 없었던 참여자 때문에 병력당 피해량이 낮아지는 일은 생기지 않습니다. 스프레드시트에서 탭으로 구분된 행을 아무 칸에나 붙여넣을 수 있습니다.',
    saveRally: 'Rally 저장',
    add5Rows: '5행 추가',
    rallySaved: 'Rally가 저장되었습니다.',
    savedJoiners: (count: number, wave: number | null) =>
      wave === null
        ? `참여자 ${count}명을 저장했습니다.`
        : `웨이브 ${wave} 에 참여자 ${count}명을 저장했습니다.`,
    weakBindings: (weak: number, total: number) =>
      `${total}개 행 중 ${weak}개는 최근 영웅/장비 스냅샷과 연결하지 못했습니다. 피해량은 기록되지만 그 뒤의 세팅은 확정되지 않아, 이 행들은 나중에 업그레이드 비교의 근거가 될 수 없습니다.`,
  },

  buildRally: {
    needsLead: 'Rally에는 리드가 필요합니다. 이 폼의 나머지 항목은 모두 선택 입력입니다.',
    fields: {
      wave: '웨이브',
      order: '순서',
      rallyCapacity: 'Rally 정원',
      actualTroops: '실제 병력',
      totalRallyDamage: 'Rally 총 피해량',
      leadHeroIds: '리드 영웅 ID',
      leadHeroIdsMax: '리드 영웅 ID (최대 3개)',
      joinOrder: '참여 순서',
      captainHero: '대장 영웅',
      infantry: '보병',
      cavalry: '기병',
      archer: '궁병',
      totalTroops: '총 병력',
      damage: '피해량',
    },
    rowField: (row: number, field: string) => `${row}행 ${field}`,
    invalidFields: (fields: string) =>
      `다음 항목은 정수이거나 비어 있어야 합니다: ${fields}. 값을 비워 두어도 괜찮으며 "알 수 없음"으로 기록됩니다 — 0으로 기록되지 않습니다.`,
    duplicatePlayers: (players: string) =>
      `같은 플레이어가 이 Rally에 두 번 이상 있습니다: ${players}.`,
  },

  rallyLab: {
    title: 'Rally 랩',
    subtitle:
      '이 연맹이 실제로 해 온 관측 기록을 바탕으로, 하나의 역할에 맞는 Rally를 구성합니다.',
    noCoreAllianceBody:
      '랩은 연맹 명단으로 Rally를 구성하는데, 아직 연맹이 설정되거나 동기화되지 않았습니다.',
    perRoleNotice:
      '점수는 역할별입니다. 여기의 추천은 다른 작전에서의 가치에 대해 아무것도 말하지 않습니다 — 강한 Bear 리드가 최고의 수비대인 경우는 오히려 드물며, 이 시스템은 그런 판단들을 하나의 숫자로 평균 내지 않고 분리해 둡니다.',
    whatAreYouBuilding: '무엇을 구성할까요',
    operation: '작전',
    side: '진영',
    attack: '공격',
    defense: '수비',
    joiners: '참여자 수',
    leadThisRally: 'Rally 리드',
    letModelChoose: '모델이 선택하게 하기',
    noRosterTitle: '동기화된 명단 없음',
    noRosterBody: '연맹 명단을 가져오기 전까지는 Rally를 구성할 인원이 없습니다.',
    availableTitle: (selected: number) => `이 Rally 가용 인원 — ${selected}명 선택됨`,
    recommend: 'Rally 추천받기',
    composing: '구성 중…',
    pickAvailableFirst: '먼저 가용 인원을 선택하세요.',

    noRoleTitle: '이 작전에는 해당 진영이 없습니다',
    noRoleBody:
      '이 조합에 대해 점수를 매길 역할이 모델에 없어, 일반적인 순위를 반환하는 대신 답변을 거부했습니다.',
    recommendedFor: (role: string) => `추천 Rally — ${role}`,
    lead: '리드',
    backupLead: '예비 리드',
    noLead: '선택한 플레이어 중에서 추천할 수 있는 리드가 없습니다.',
    noBackup: '이 명단에서 가용한 예비 인원이 없습니다.',
    leadHeroes: '리드 영웅',
    noHeroRecommendation: '영웅 추천 없음 — 근거가 될 기록이 없습니다.',
    rawHeroId: (heroId: number | string) => `영웅 #${heroId}`,
    computedFrom: (when: string) => `${when} 기준 데이터로 산출`,
    rules: (version: string) => ` · 규칙 ${version}`,
    run: (runId: string) => ` · 실행 ${runId}`,
    notPersisted: ' · 저장되지 않음',
    joinersTitle: '참여자',
    noJoiners: '선택한 플레이어 중에서 추천된 참여자가 없습니다.',
    joinerColumns: {
      rank: '#',
      score: '점수',
      captainHero: '대장 영웅',
    },
    noRecommendation: '추천 없음',

    troopComposition: '병력 편성',
    /**
     * 초기에는 이쪽이 기본값이다. 커뮤니티에 널리 퍼진 비율이 있지만, 달리 근거가
     * 없다는 이유로 그것을 찍어 내면 소문이 이 시스템의 추천이 되어 버린다.
     */
    noComposition: '추천 편성 없음',
    noCompositionBody:
      '이 역할에 대한 병력 배분을 뒷받침할 기록이 아직 없습니다. 커뮤니티에 널리 퍼진 비율이 있지만 여기서 아무도 측정한 적이 없으므로, 이 시스템은 그것을 추천으로 제시하지 않습니다. 병력 수가 포함된 Rally를 몇 건 기록하면 비교가 가능해집니다.',
    normalized: (value: string) => `정규화 ${value}배`,
    normalizedTitle: '이 역할에 대한 이 연맹 자체의 기준선 대비 값입니다.',

    missingTitle: '순위를 매길 수 없었던 인원',
    missingBlurb:
      '이 플레이어들은 추측 점수를 주는 대신 순위에서 제외했습니다. 여기에 있다는 것은 우리 기록의 공백이지, 그 사람에 대한 평가가 아닙니다.',
  },

  events: {
    plannerNotAutomated: '이 작전의 계획 수립은 아직 자동화되지 않았습니다.',
    plannerLands: (phase: string) =>
      ` 해당 기능은 ${phase} 에서 제공됩니다. 아래의 일정 등록과 인원 편성은 실제로 동작합니다 — 이 화면에 자리만 채워 둔 것은 없습니다.`,
    newEvent: (title: string) => `새 ${title}`,
    name: '이름',
    starts: '시작',
    schedule: '일정 등록',
    scheduling: '등록 중…',
    optionalFieldsNote:
      '두 항목 모두 선택 입력입니다 — 시작 시각이 없는 작전도 기록해 둘 가치가 있으며, 빈 시작 시각은 자정이 아니라 "아직 일정 미정"으로 읽힙니다.',
    scheduled: '등록된 일정',
    loadingEvents: '작전 불러오는 중',
    nothingScheduledTitle: '아직 등록된 일정 없음',
    nothingScheduledBody:
      '작전은 R4가 직접 생성하며, 게임에서 추론하지 않습니다. 위에서 일정을 등록하면 여기에 나타납니다.',
    columns: {
      name: '이름',
      starts: '시작',
      status: '상태',
      created: '생성',
    },
    notScheduled: '일정 미정',
    roster: '인원',
    cancelTitle: '이 작전을 취소 상태로 표시합니다. 기록에는 그대로 남으며 삭제되지 않습니다.',
    cancelNote:
      '취소하면 작전이 취소 상태로 기록에 남습니다. 이곳에서는 아무것도 삭제되지 않습니다.',

    loadingRoster: '인원 불러오는 중',
    rosterFor: '인원 —',
    cancelledNotice:
      '이 작전은 취소되었습니다. 무엇을 계획했는지에 대한 기록을 온전히 남기기 위해 보관하며, 아래 인원은 당시 편성 그대로입니다.',
    /** 인원 배치 정보이지 서열이 아니다 (§50). */
    rosterBlurb:
      '참여 예정 인원입니다. 작전 인원을 채우기 위한 배치 정보이며, 멤버를 서열화하기 위한 것이 아닙니다.',
    loadingAllianceRoster: '연맹 명단 불러오는 중',
    noAllianceRosterTitle: '동기화된 연맹 명단 없음',
    noAllianceRosterBody:
      '참여자는 연맹 명단에서 고르는데, 아직 명단을 가져오지 않았습니다.',
    saveParticipants: (count: number) => `참여자 ${count}명 저장`,
    selectAtLeastOne: '플레이어를 한 명 이상 선택하세요 — API는 빈 명단을 받지 않습니다.',
    savedCount: (count: number) => `${count}명 저장했습니다.`,
    recordedParticipants: '기록된 참여자',
    participantColumns: {
      player: '플레이어',
      role: '역할',
      team: '팀',
      attendance: '참여',
      notes: '메모',
    },
    unassigned: '미배정',

    championship: {
      title: 'Alliance Championship',
      subtitle: '라운드 일정을 잡고, 가용 인원을 기록하고, 레인을 배정합니다.',
      plannerNote:
        '레인 배정과 승률 예측은 아직 만들어지지 않았습니다. 제공될 때 초기 확률은 휴리스틱이며, 그렇다고 표시하고 전제도 함께 나열할 것입니다 — 기록된 라운드가 하나도 없는 모델이 내놓은 68%는 퍼센트 기호를 붙인 추측일 뿐입니다.',
      plannerPhase: 'AC 레인 최적화 및 상대 패턴 기록 (스펙 §40-42)',
    },
    swordland: {
      title: 'Swordland',
      subtitle: '이벤트 일정을 잡고 인원을 기록합니다.',
      plannerNote:
        '군단 편성은 아직 만들어지지 않았습니다. 군단 규모 제한과 역할 규칙은 검증되지 않은 게임 메커닉이므로 하드코딩하지 않고 UNKNOWN 전략 규칙으로 저장합니다. 편성기는 아무도 확인하지 않은 제한에 몰래 의존하는 명단을 만들어 내지 않습니다.',
      plannerPhase: 'Swordland 인원 편성 (스펙 §43)',
    },
    fortress: {
      title: 'Sanctuary / Fortress',
      subtitle: '작전 일정을 잡고 공격 인원과 수비대를 기록합니다.',
      plannerNote:
        '공격 순서, 시차 오프셋, 수비대 배정은 아직 만들어지지 않았습니다. 타이밍 오프셋은 설정 가능한 규칙이나 이 연맹 자체의 기록에서 가져올 것이며, 존재 여부조차 검증되지 않은 보편 상수에서 가져오지 않습니다.',
      plannerPhase: '구조물 작전 (스펙 §38)',
    },
    castle: {
      title: 'Castle',
      subtitle: 'Castle 작전 일정을 잡고 인원을 기록합니다.',
      plannerNote: '공격 순서와 수비대 인원 배치 추천은 아직 만들어지지 않았습니다.',
      plannerPhase: 'Castle 작전 (스펙 §39)',
    },
    viking: {
      title: 'Viking Vengeance',
      subtitle: '이벤트 일정을 잡고 참여 인원을 기록합니다.',
      plannerNote:
        '짝 편성과 지원 배정은 아직 만들어지지 않았습니다. 제공될 때는 전투력이 아니라 기록된 결과에서 도출할 것입니다.',
      plannerPhase: 'Viking Vengeance 지원 (스펙 §44)',
    },
    triAlliance: {
      title: 'Tri-Alliance Clash',
      subtitle: 'Clash 일정을 잡고 우리 쪽 인원을 기록합니다.',
      plannerNote:
        '연맹 간 협조 기능은 아직 만들어지지 않았습니다. 우리는 우리 연맹의 데이터만 볼 수 있으므로, 나머지 두 연맹까지 아우르는 계획은 공백 위에 세워질 수밖에 없습니다.',
      plannerPhase: '이후 단계 (스펙 §57)',
    },
    mobilization: {
      title: 'Mobilization / Brawl',
      subtitle: '기간을 등록하고 참여 인원을 기록합니다.',
      plannerNote:
        '점수 기여도와 과제 커버리지 추적은 아직 만들어지지 않았습니다. 여기의 참여 기록은 인원 배치와 계획을 위한 것이며, 멤버 서열이 아니고 앞으로도 그렇게 되지 않습니다.',
      plannerPhase: 'Mobilization 추적 (스펙 §45)',
    },
    experiments: {
      title: 'Bear 실험',
      subtitle: '변수 하나를 충분히 오래 고정해 두고 무언가를 알아내기.',
      notice:
        '개인 분석의 업그레이드 영향 패널은 두 번의 실제 세션 사이에 여러 가지가 함께 바뀌기 때문에 연관성까지만 보고할 수 있습니다. 실험은 그것을 해결하기 위한 기능입니다 — 변수 하나를 고르고 나머지를 고정한 채 충분한 세션에 걸쳐 진행하면, 감보다 나은 결과를 얻을 수 있습니다.',
      feature: '실험',
      phase: '현재 빌드 범위 밖인 4단계 (스펙 §28)',
      describes:
        '가설을 정의하고, 어떤 세션이 그 가설에 속하는지 표시하고, 샘플 수와 신뢰도와 함께 결과를 읽습니다.',
    },
  },

  stateIntel: {
    noKingdomTitle: '킹덤이 설정되지 않음',
    noKingdomBody:
      '스테이트 정보는 설정된 킹덤 범위로 동작합니다. 먼저 서버에서 설정하고 동기화하세요.',

    alliances: {
      title: '스테이트 연맹',
      subtitle: (kingdomId: number) => `킹덤 ${kingdomId} 에서 관측된 연맹입니다.`,
      loading: '연맹 불러오는 중',
      emptyTitle: '동기화된 연맹 없음',
      emptyBody: '킹덤 연맹 목록을 아직 가져오지 않았습니다.',
      ours: '우리',
      columns: {
        alliance: '연맹',
        level: '레벨',
        power: '전투력',
        kills: '처치 수',
        members: '인원',
        leader: '리더',
        age: '경과',
      },
    },

    players: {
      title: '스테이트 플레이어',
      subtitle: '이 시스템이 저장한 적 있는 킹덤 내 모든 연맹의 플레이어입니다.',
      searchPlaceholder: '이름으로 검색',
      searchAria: '플레이어 검색',
      loading: '플레이어 불러오는 중',
      noMatchTitle: '검색 조건에 맞는 플레이어 없음',
      noMatchBody: '최소 한 번 이상 동기화된 플레이어만 검색할 수 있습니다.',
      emptyTitle: '아직 저장된 플레이어 없음',
      emptyBody: '플레이어는 킹덤 및 연맹 동기화가 발견하는 대로 저장됩니다.',
      showing: (shown: number, total: string) =>
        `저장된 플레이어 ${total}명 중 ${shown}명을 표시하고 있습니다.`,
      noMatch: '일치하는 플레이어가 없습니다.',
    },

    transfers: {
      title: '연맹 이동',
      subtitle: '킹덤 전역에서 감지된 연맹 이동입니다.',
      loading: '이동 내역 불러오는 중',
      emptyTitle: '이 기간에 감지된 이동 없음',
      emptyBody:
        '이동은 명단 스냅샷 비교로 찾아냅니다. 조용한 기간은 실패가 아니라 정상적인 결과입니다.',
    },

    leaderboards: {
      title: '리더보드',
      subtitle: '킹덤 리더보드 스냅샷.',
      feature: '리더보드',
      phase: '리더보드 저장을 추가하는 단계 (스펙 §10.3)',
      describes:
        '리더보드 각 보드는 상위 API에서 대략 하루 주기로 갱신되므로, 이 화면은 공통 타임스탬프 하나가 아니라 보드별 경과 시간을 표시합니다.',
    },

    kvk: {
      title: 'KvK',
      subtitle: '킹덤 간 대전 순위와 매치업.',
      feature: 'KvK',
      phase: 'KvK 수집을 추가하는 단계 (스펙 §11)',
      describes:
        'KvK 엔드포인트를 주기적으로 수집·저장하기 시작하면 매치업, 순위, 킹덤별 기여도를 표시합니다.',
    },
  },

  data: {
    sync: {
      title: '동기화 상태',
      subtitle: '백그라운드 작업이 무엇을 가져왔고 무엇을 가져오지 못했는지입니다.',
      loading: '동기화 상태 불러오는 중',
      budgetCritical: (percent: number) =>
        `오늘 상위 API 요청 예산의 ${percent}% 를 사용했습니다. 동기화가 건너뛰어지기 시작하면, 앱 곳곳의 최신성 수치는 오류를 띄우는 대신 조용히 나빠집니다.`,
      rateLimited: (count: number) =>
        `오늘 요청 ${count}건이 한도 제한을 받았습니다. 해당 리소스는 예정된 주기보다 오래되었습니다.`,
      requestsUsed: '오늘 사용한 요청',
      requestsUsedValue: (used: string, budget: string) => `${used} / ${budget}`,
      budgetFoot: (percent: number) => `예산의 ${percent}%`,
      rateLimitedToday: '오늘 한도 제한',
      queueDepth: '대기열 길이',
      lastKingdomSync: '마지막 킹덤 동기화',
      lastAllianceSync: '마지막 연맹 동기화',
      ago: (duration: string) => `${duration} 전`,
      neverRun: '실행된 적 없음',
      failuresTitle: '최근 24시간 내 실패',
      noFailures:
        '최근 24시간 동안 실패가 없습니다. 다만 한 번도 실행되지 않은 작업 역시 실패를 남기지 않으므로, 위의 마지막 동기화 시각을 함께 확인하세요.',
      failureColumns: {
        finished: '종료 시각',
        job: '작업',
        resource: '리소스',
        kind: '종류',
        message: '메시지',
      },
      unknownKind: '알 수 없음',
      noMessage: '메시지 없음',
      staleTitle: '오래된 플레이어',
      staleBlurb:
        '예상 주기 안에 프로필이 갱신되지 않은 플레이어입니다. 이들이 포함된 분석은 나머지 명단보다 오래된 입력으로 계산됩니다.',
      nothingBehind: '일정보다 늦어진 항목이 없습니다.',
      staleColumns: {
        player: '플레이어',
        age: '경과',
      },
    },

    freshness: {
      title: '최신성',
      subtitle: '각 소스가 자신의 시계 기준으로 얼마나 오래되었는지입니다.',
      /** §64: 전역 타임스탬프 하나로 합치지 않는다. */
      notice:
        '이 앱 어디에도 단일 "마지막 업데이트" 표시는 의도적으로 두지 않았습니다. 플레이어 프로필은 상위 API에서 대략 10분마다, 처치 수와 순위는 약 6시간 주기의 랭킹 보드에서, 리더보드는 대략 하루 주기로 갱신됩니다. 하나로 합친 타임스탬프는 그중 어느 것에 대해서도 정확하지 않습니다.',
      loading: '최신성 불러오는 중',
      kingdom: '킹덤',
      allianceRoster: '연맹 명단',
      neverFetched: '가져온 적 없음',
      perPlayerTitle: '플레이어별 최신성',
      perPlayerBlurb:
        '각 플레이어는 프로필, 처치 수, 순위, 영웅 장비 네 개의 독립적인 시계를 가지며, 상세 페이지 상단에 표시됩니다. 여기에는 현재 일정보다 늦어진 플레이어만 나열합니다.',
      nonePending: '갱신 일정보다 늦어진 플레이어가 없습니다.',
      playerAge: (duration: string) => `— ${duration} 경과`,
    },

    rules: {
      title: '전략 규칙',
      subtitle: '이 시스템이 안다고 주장하지 않는 게임 메커닉입니다.',
      notice:
        '불확실한 메커닉 — 참여자 스킬 선택, 최적 병력 비율, 영웅 스타 인코딩, 장비 ID 인코딩, Rally 시차 오프셋 — 은 하드코딩하지 않고 UNKNOWN 상태의 규칙으로 저장합니다. 이 화면은 그것들을 검토하고, 연맹이 경험적으로 확립했을 때 승격시키는 곳입니다.',
      feature: '규칙 편집',
      phase: '이 화면에서 규칙을 직접 수정할 수 있게 되는 단계 (스펙 §22)',
      loading: '전략 규칙 불러오는 중',
      noRulesTitle: '전략 규칙 없음',
      noRulesBody: '아직 기록된 규칙이 없습니다. 규칙은 마이그레이션으로 들어옵니다.',
      active: '적용 중',
      superseded: '대체됨',
      verified: '공식 문구',
      unscored: '미평가',
      confidenceLabel: (c: number) => `신뢰도 ${c.toFixed(2)}`,
      confidenceTitle:
        'Kingshot에 한해 이 내용이 사실일 확률입니다. 0.8 이상은 공식 문구이거나 교차 검증됨, 0.6~0.8은 독립된 출처 2곳, 0.4~0.6은 출처 1곳이거나 여러 곳이 한 원본을 공유, 0.4 미만은 근거가 약하거나 서로 엇갈림.',
      showConfig: '저장된 값 보기',
      source: '출처',
      noSource: '출처 없음',
      effectiveFrom: '적용 시작',
      describes: '각 규칙의 현재 상태, 그 근거, 그리고 마지막으로 변경한 사람.',
    },

    catalog: {
      title: '카탈로그',
      subtitle: '영웅·스킬·장비 ID와, 그에 대해 확인된 내용입니다.',
      feature: '카탈로그 열람',
      phase: '카탈로그 테이블을 API로 노출하는 단계 (스펙 §9)',
      describes:
        '그전까지 인식되지 않은 ID는 앱 전반에서 이름을 추측하지 않고 "미검증" 표시가 붙은 원본 ID로 표시됩니다 — 아무 플레이어의 영웅 탭에서 확인할 수 있습니다.',
    },

    settings: {
      title: '설정',
      subtitle: '이 브라우저 세션이 무엇에 연결되어 있는지입니다.',
      connection: '연결',
      apiBaseUrl: 'API 기본 URL',
      supabaseProject: 'Supabase 프로젝트',
      signedInAs: '로그인 계정',
      notSignedIn: '로그인되지 않음',
      kingdom: '킹덤',
      coreAlliance: '기준 연맹',
      notConfigured: '설정되지 않음',
      language: '언어',
      whereConfigTitle: '설정이 있는 곳',
      whereConfigBody:
        '킹덤, 기준 연맹, 동기화 등급, API 예산은 브라우저 환경설정이 아니라 서버 측 설정입니다. 여기서 편집할 수 없게 해 둔 것은 의도적입니다: 이 번들은 공개되며, 여기서 바꿀 수 있는 것은 여기서 유출될 수도 있습니다.',
      tokenNote:
        '상위 API 토큰은 Worker 시크릿에만 존재합니다. 이 앱은 그 토큰을 가진 적이 없으며, 요청할 수 있는 코드 경로도 없습니다.',
      notEditableTitle: '워크스페이스 설정은 아직 편집할 수 없습니다',
      notEditableBody:
        '킹덤, 기준 연맹, 동기화 설정을 편집하려면 아직 존재하지 않는 인증된 설정 API가 필요합니다. 당분간은 Worker 설정에서 변경합니다.',
    },
  },

  notFound: {
    title: '찾을 수 없음',
    emptyTitle: '해당 화면 없음',
    emptyBody: '이 앱에는 그런 경로가 없습니다.',
    back: '개요로 돌아가기',
  },
};
